import React, { useState, useEffect } from 'react';
import {
  Pane,
  Button,
  Heading,
  Text,
  Card,
  StatusIndicator,
  Select,
  toaster,
  Spinner
} from 'evergreen-ui';
import { useProject } from '../context/ProjectContext';
import api from '../services/api';

function Workers() {
  const { currentProject } = useProject();
  const [workerStatus, setWorkerStatus] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  useEffect(() => {
    if (currentProject?.id) {
      fetchWorkerStatus();
    }
  }, [currentProject]);

  const fetchWorkerStatus = async () => {
    if (!currentProject?.id) return;
    
    try {
      setIsLoading(true);
      const response = await api.get(`/worker/status/${currentProject.id}`);
      setWorkerStatus(response.data);
    } catch (error) {
      toaster.danger('Failed to fetch worker status');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownloadConfig = async () => {
    if (!currentProject?.id) return;
    
    try {
      setIsDownloading(true);
      const response = await api.get(`/worker/configure/${currentProject.id}`, {
        responseType: 'blob'
      });

      // Extract headers
      const redisPassword = response.headers['x-redis-password'];
      const taskQueue = response.headers['x-task-queue'];
      const dataQueue = response.headers['x-data-queue'];

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `docker-compose-${currentProject.id}.yml`);
      document.body.appendChild(link);
      link.click();
      link.remove();

      toaster.success('Configuration downloaded successfully');
      
      // Show Redis credentials in a separate toast
      toaster.notify(
        'Worker credentials have been copied to your clipboard',
        {
          duration: 10,
          description: 'Please save these credentials securely.'
        }
      );

      // Copy credentials to clipboard
      const credentials = `Redis Password: ${redisPassword}\nTask Queue: ${taskQueue}\nData Queue: ${dataQueue}`;
      await navigator.clipboard.writeText(credentials);

    } catch (error) {
      toaster.danger('Failed to download worker configuration');
    } finally {
      setIsDownloading(false);
    }
  };

  if (!currentProject) {
    return (
      <Pane padding={16}>
        <Text>Please select a project first</Text>
      </Pane>
    );
  }

  return (
    <Pane padding={16} maxWidth={1200} marginX="auto">
      <Pane display="flex" justifyContent="space-between" alignItems="center" marginBottom={24}>
        <Heading size={700}>Worker Configuration</Heading>
        <Button
          appearance="primary"
          intent="success"
          onClick={handleDownloadConfig}
          isLoading={isDownloading}
        >
          Download Worker Config
        </Button>
      </Pane>

      <Card
        backgroundColor="white"
        elevation={1}
        padding={16}
        marginBottom={16}
      >
        <Pane>
          <Heading size={500} marginBottom={8}>Project Details</Heading>
          <Text display="block">Project ID: {currentProject.id}</Text>
          <Text display="block">Project Name: {currentProject.name}</Text>
        </Pane>
      </Card>

      <Card
        backgroundColor="white"
        elevation={1}
        padding={16}
      >
        <Heading size={500} marginBottom={16}>Worker Status</Heading>
        {isLoading ? (
          <Pane display="flex" alignItems="center" justifyContent="center" padding={32}>
            <Spinner />
          </Pane>
        ) : workerStatus ? (
          <Pane>
            <Pane display="flex" alignItems="center" marginBottom={8}>
              <StatusIndicator color="success" marginRight={8} />
              <Text>Queues Configured</Text>
            </Pane>
            <Text display="block">Task Queue: {workerStatus.task_queue}</Text>
            <Text display="block">Data Queue: {workerStatus.data_queue}</Text>
            <Text display="block">Event Queue: {workerStatus.event_queue}</Text>
          </Pane>
        ) : (
          <Text>No worker status available</Text>
        )}
      </Card>
    </Pane>
  );
}

export default Workers; 